import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState(params.get("role") === "streamer" ? "streamer" : "fan");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : { ...form, role };
      const { data } = await api.post(`/auth/${mode === "login" ? "login" : "register"}`, payload);
      setUser(data);
      toast.success(t("auth_welcome", { name: data.name }));
      if (data.role === "admin") navigate("/admin");
      else if (data.role === "streamer") navigate("/dashboard");
      else navigate("/descubrir");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <h1 className="text-4xl font-black tracking-tighter text-white mb-2">
          {mode === "login" ? t("auth_login_title") : t("auth_register_title")}
        </h1>
        <p className="text-sm text-zinc-400 mb-8">
          {mode === "login" ? t("auth_login_sub") : t("auth_register_sub")}
        </p>
        <form onSubmit={submit} className="space-y-5 bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8">
          {mode === "register" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" data-testid="role-streamer-btn"
                  onClick={() => setRole("streamer")}
                  className={`rounded-xl border p-4 text-left transition-colors ${role === "streamer" ? "border-emerald-500 bg-emerald-600/15" : "border-zinc-800 hover:border-zinc-700"}`}>
                  <span className="block text-sm font-semibold text-white">{t("auth_role_streamer")}</span>
                  <span className="block text-xs text-zinc-400 mt-1">{t("auth_role_streamer_desc")}</span>
                </button>
                <button type="button" data-testid="role-fan-btn"
                  onClick={() => setRole("fan")}
                  className={`rounded-xl border p-4 text-left transition-colors ${role === "fan" ? "border-cyan-500 bg-cyan-600/15" : "border-zinc-800 hover:border-zinc-700"}`}>
                  <span className="block text-sm font-semibold text-white">{t("auth_role_fan")}</span>
                  <span className="block text-xs text-zinc-400 mt-1">{t("auth_role_fan_desc")}</span>
                </button>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">{t("auth_name")}</Label>
                <Input data-testid="auth-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-white" placeholder={t("auth_name_ph")} />
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label className="text-zinc-300">{t("auth_email")}</Label>
            <Input data-testid="auth-email-input" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-white" placeholder="you@email.com" />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">{t("auth_password")}</Label>
            <Input data-testid="auth-password-input" type="password" required minLength={6} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-white" placeholder="••••••••" />
          </div>
          <Button data-testid="auth-submit-btn" type="submit" disabled={loading}
            className="w-full rounded-full bg-emerald-600 hover:bg-emerald-500 text-white h-11">
            {loading ? t("auth_processing") : mode === "login" ? t("auth_submit_login") : t("auth_submit_register")}
          </Button>
        </form>
        <button data-testid="google-login-btn" type="button"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            localStorage.setItem("liveq_google_role", mode === "register" ? role : "fan");
            const redirectUrl = window.location.origin + "/dashboard";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="mt-4 w-full flex items-center justify-center gap-3 rounded-full border border-zinc-700 bg-white text-zinc-900 font-semibold h-11 text-sm hover:bg-zinc-200 transition-colors">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 40.1 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
          {t("auth_google")}
        </button>
        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-xs text-zinc-600">{t("auth_or")}</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>
        <button data-testid="auth-toggle-mode-btn"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-6 text-sm text-zinc-400 hover:text-white transition-colors w-full text-center">
          {mode === "login" ? t("auth_to_register") : t("auth_to_login")}
        </button>
      </div>
    </div>
  );
}
